import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { fileUrl } from '../api/client.ts';
import type { MessageView } from '../chat/ChatContext.tsx';
import { formatDuration, formatSendingLabel } from '../lib/format.ts';
import { ringFraction } from '../lib/noteRing.ts';
import { SCRUB_STEP, nudge, trackFraction } from '../lib/scrub.ts';
import {
  transcriptAction,
  transcriptNotice,
  transcriptShown,
  transcriptState,
} from '../lib/transcript.ts';
import { useInView } from '../lib/useInView.ts';
import { waveformFor } from '../lib/waveform.ts';
import { PauseIcon, PlayIcon, SpeakerIcon } from './MediaIcons.tsx';

/**
 * The two recorded message kinds in a thread: a кружочек and a voice message.
 *
 * They ask different things of the reader. A voice message is played: it is
 * started by a tap, one at a time, because two people talking at once is
 * useless. A кружочек is watched: it runs by itself and silently, so nothing has
 * to be started and nothing can interrupt anything else.
 *
 * Both drive a real media element rather than a timer. Progress comes from
 * `timeupdate`, so a bar that says half way through means the file is half way
 * through — including when a slow connection stalls it.
 *
 * Neither reads the element's own `duration`: a WebM written by MediaRecorder
 * frequently reports `Infinity`, and every length in the app — the pill, the
 * remaining time, the width of the played waveform — comes from `durationMs`,
 * which the sender measured while recording.
 */

/**
 * Where playback starts and stops, honouring the sender's trim.
 *
 * `stopAtMs` is null unless the sender actually chose an out-point, and that null
 * matters: it used to be `durationMs`, which is a *measurement* — wall-clock time
 * between starting and stopping the recorder — and stopping playback at a
 * measurement throws away however much the file runs past it. That is where the
 * last word went. An untrimmed recording now plays until the element says it has
 * ended, and durationMs is left to do what it is good for: labels and progress.
 */
function rangeOf(message: MessageView): {
  startMs: number;
  stopAtMs: number | null;
  lengthMs: number;
} {
  const total = message.durationMs ?? 0;
  const startMs = message.trimStartMs ?? 0;
  const endMs = message.trimEndMs ?? total;
  return { startMs, stopAtMs: message.trimEndMs, lengthMs: Math.max(0, endMs - startMs) };
}

/** How long the recording actually plays for — the kept range, not the file. */
export function playableMs(message: MessageView): number {
  return rangeOf(message).lengthMs;
}

interface PlaybackProps {
  message: MessageView;
  /** The thread decides who plays: only one voice message sounds at a time. */
  playing: boolean;
  onToggle: () => void;
  onEnded: () => void;
}

/**
 * Keeps one media element in step with the thread's idea of what is playing,
 * and reports how far through it is.
 *
 * The element is the source of truth for position and the caller is the source
 * of truth for whether it should be running. That split is what lets one hook
 * serve both kinds: "the thread picked me" for a voice message and "I am on
 * screen" for a кружочек are the same question as far as this is concerned.
 */
function usePlayback(
  element: HTMLMediaElement | null,
  { message, playing, onEnded }: Omit<PlaybackProps, 'onToggle'>,
  speed = 1,
  /**
   * Whether stopping keeps the position. A voice message is seekable, and a
   * reader who has just put the playhead somewhere and paused means to carry on
   * from there. A кружочек is not: "it left the screen" honestly means "from the
   * top", and there is no control on it that could say otherwise.
   */
  remember = false,
) {
  const { startMs, stopAtMs, lengthMs } = rangeOf(message);
  const [positionMs, setPositionMs] = useState(0);
  const ended = useRef(onEnded);
  ended.current = onEnded;
  // Read by the play effect below, which must not re-run every time the
  // position moves — that is once every few hundred milliseconds.
  const resumeMs = useRef(0);
  resumeMs.current = remember ? positionMs : 0;

  useEffect(() => {
    if (!element) return;
    element.playbackRate = speed;

    if (!playing) {
      element.pause();
      return;
    }
    // Where this take should pick up: its in-point, plus however far into it
    // the reader had got. Both are zero in the ordinary case, and an element
    // that ran to the end rewinds itself on play(), so the common path writes
    // nothing. Seeking a WebM without an index can be refused, in which case
    // playback simply begins at zero — the out-point below still holds, so the
    // range is never overrun.
    const wanted = startMs + resumeMs.current;
    if (wanted > 0 && Math.abs(element.currentTime * 1000 - wanted) > 250) {
      element.currentTime = wanted / 1000;
    }
    void element.play().catch(() => ended.current());
  }, [element, playing, speed, startMs]);

  // Position resets when this stops being the playing message, so the next tap
  // starts from the beginning rather than from where it was interrupted —
  // unless the caller asked to keep it.
  useEffect(() => {
    if (!playing && !remember) setPositionMs(0);
  }, [playing, remember]);

  /**
   * Перемотка: put the playhead at a fraction of the kept range.
   *
   * The position moves now and the element catches up on the next frame. A seek
   * per pointermove outruns the decoder and the readout stutters behind the
   * finger; a seek per frame, always to the newest target, keeps up. Same
   * throttle as the кружочек's ring and the trim screen's handles.
   */
  const seekFrame = useRef<number | null>(null);
  const target = useRef(0);
  const seekTo = useCallback(
    (fraction: number) => {
      const at = Math.min(1, Math.max(0, fraction)) * lengthMs;
      setPositionMs(at);
      target.current = startMs + at;
      if (seekFrame.current !== null) return;
      seekFrame.current = requestAnimationFrame(() => {
        seekFrame.current = null;
        if (element) element.currentTime = target.current / 1000;
      });
    },
    [element, startMs, lengthMs],
  );

  useEffect(
    () => () => {
      if (seekFrame.current !== null) cancelAnimationFrame(seekFrame.current);
    },
    [],
  );

  const onTimeUpdate = () => {
    if (!element) return;
    const at = element.currentTime * 1000;
    // Only a chosen out-point stops it early; otherwise the file's own end does,
    // through onEnded below.
    if (stopAtMs !== null && at >= stopAtMs) {
      element.pause();
      setPositionMs(0);
      ended.current();
      return;
    }
    setPositionMs(Math.max(0, at - startMs));
  };

  return {
    positionMs,
    /** 0–100 through the kept range, which is what the ring and bars draw. */
    percent: lengthMs > 0 ? Math.min(100, (positionMs / lengthMs) * 100) : 0,
    remainingMs: Math.max(0, lengthMs - positionMs),
    lengthMs,
    seekTo,
    onTimeUpdate,
    onEnded: () => {
      setPositionMs(0);
      ended.current();
    },
  };
}

/** «Отправляем 42%» — the overlay while the bytes are going up, or being made. */
function uploadLabel(message: MessageView): string | null {
  if (message.status !== 'sending') return null;
  return formatSendingLabel(message.sendPhase ?? 'uploading', message.progressPct ?? 0);
}

/**
 * A кружочек: a moving portrait that a tap makes bigger, and audible.
 *
 * It is not a video player, which is the whole difference from the voice bubble
 * below. There is still no play button and no sound control, because there is
 * still nothing to decide — a кружочек on screen is always running — but size
 * and sound are now one fact rather than two. Small is a portrait in the corner
 * of a conversation and stays silent; big is you watching it, and something you
 * deliberately enlarged is something you meant to hear.
 *
 * Coupling them is also what keeps sound safe. Autoplay policy forbids a video
 * from making noise unprompted, and rightly: a circle that could start talking
 * out of a scroll is the behaviour nobody wanted. Unmuting only ever happens
 * inside the tap that enlarges it, which is exactly the user gesture the policy
 * asks for. `expanded` is owned by the thread, so only one is ever big — and
 * therefore only one can ever be heard.
 */
export function VideoNoteBubble({
  message,
  expanded,
  onToggleExpand,
}: {
  message: MessageView;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  // What the element says it is doing, not what we asked of it. Autoplay can be
  // refused — iOS in low-power mode, or a browser told never to play media — and
  // the ▶ badge below is drawn from this rather than from our intent, so it
  // appears exactly when there is something for a tap to fix.
  const [paused, setPaused] = useState(true);
  const note = useRef<HTMLDivElement>(null);
  const inView = useInView(element);

  const { startMs } = rangeOf(message);
  const playback = usePlayback(element, {
    message,
    // A note off screen is paused, which is what keeps `preload="none"` from
    // downloading a thread's worth of video nobody scrolled to. An expanded one
    // is on screen by definition.
    playing: inView || expanded,
    // Looping through `onEnded` rather than the element's own `loop` attribute:
    // `loop` restarts the file, and a trimmed кружочек has to restart at its
    // in-point and stop again at its out-point. usePlayback already knows both.
    onEnded: () => {
      if (!element) return;
      element.currentTime = startMs / 1000;
      void element.play().catch(() => {});
    },
  });
  const uploading = uploadLabel(message);

  /**
   * Sound follows size, and is applied to the element rather than passed as a
   * prop. React does set `muted` as a DOM property, but the element is also
   * created and swapped by a ref callback here, and an effect is the one place
   * that is guaranteed to run after both that and every later change to
   * `expanded` — which is what makes "big means audible" true of the element
   * and not just of the render that asked for it.
   *
   * Being on screen is the other half of it. An enlarged note goes on playing
   * when it is scrolled past, which was unremarkable while it was silent and is
   * not now: a voice with nothing on screen to explain it is worse than a
   * paused portrait. Scrolling back brings the sound with it.
   */
  useEffect(() => {
    if (!element) return;
    element.muted = !expanded || !inView;
  }, [element, expanded, inView]);

  /**
   * Перемотка, on the only timeline a кружочек has: the ring. The enlarged
   * note grows a knob riding the arc, and dragging it around the circle seeks —
   * the maths of «where around the ring is this finger» is lib/noteRing.ts.
   *
   * `scrub` is the fraction under the finger, and while it is non-null it is
   * what the ring, the knob and the countdown draw from — the element is being
   * seeked a rAF behind it, and honesty to the file matters less mid-gesture
   * than a knob that keeps up with the hand. The element is paused for the
   * drag: left running, reaching the out-point would trip the loop in
   * usePlayback and yank the take back to its in-point under the finger, which
   * is also why onTimeUpdate below is gated on the drag. Release plays on from
   * wherever the knob was left.
   */
  const [scrub, setScrub] = useState<number | null>(null);
  /** The circle's centre, in viewport coordinates, for as long as a drag holds. */
  const scrubbing = useRef<{ x: number; y: number } | null>(null);
  const seekFrame = useRef<number | null>(null);
  const fraction = useRef(0);

  const applyScrub = useCallback(
    (clientX: number, clientY: number) => {
      const center = scrubbing.current;
      if (!center) return;
      fraction.current = ringFraction(center, { x: clientX, y: clientY });
      setScrub(fraction.current);
      // A seek per pointermove outruns the decoder and the picture stutters
      // behind the finger; a seek per frame, always to the newest position,
      // keeps up. Same throttle as the trim screen's handles.
      if (seekFrame.current !== null) return;
      seekFrame.current = requestAnimationFrame(() => {
        seekFrame.current = null;
        if (element && scrubbing.current) {
          element.currentTime = (startMs + fraction.current * playback.lengthMs) / 1000;
        }
      });
    },
    [element, startMs, playback.lengthMs],
  );

  const grabKnob = (event: ReactPointerEvent) => {
    const host = note.current;
    if (!host || !element) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = host.getBoundingClientRect();
    scrubbing.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    element.pause();
    applyScrub(event.clientX, event.clientY);
  };

  // On the window, like every drag in this app: the point of the gesture is
  // that the finger circles the ring, which leaves the knob almost at once.
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!scrubbing.current) return;
      if (event.cancelable) event.preventDefault();
      applyScrub(event.clientX, event.clientY);
    };
    const onUp = () => {
      if (!scrubbing.current) return;
      scrubbing.current = null;
      setScrub(null);
      // The drag paused it; letting go is the same gesture ending.
      void element?.play().catch(() => {});
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [applyScrub, element]);

  useEffect(
    () => () => {
      if (seekFrame.current !== null) cancelAnimationFrame(seekFrame.current);
    },
    [],
  );

  // Accent while it is still new to this reader, grey once watched. It is the
  // *track* that carries this, not the arc: everything on screen is playing now,
  // so a state that only showed while stopped would never be seen at all.
  const unwatched = !message.fromMe && message.viewedAt === null;
  const track = unwatched ? 'var(--unwatched-track)' : 'var(--border)';
  // A note paused by the knob is still a running one as far as the ring goes:
  // the arc under the finger is the gesture's whole readout.
  const running = (!paused || scrub !== null) && !uploading;
  const shownPercent = scrub !== null ? scrub * 100 : playback.percent;
  const ring = running
    ? `conic-gradient(var(--accent) ${shownPercent}%, ${track} 0)`
    : unwatched
      ? 'var(--accent)'
      : 'var(--border)';

  const toggle = () => {
    if (uploading) return;
    onToggleExpand();
    // The tap is a user gesture, which is the one thing that gets playback out
    // of a refused autoplay. Without this, tapping a note the browser declined
    // to start would enlarge a still frame and leave it still.
    void element?.play().catch(() => {});
  };

  return (
    <div
      ref={note}
      className={`note${expanded ? ' note-expanded' : ''}`}
      // The enlarged ring is a point wider: it is a control now, not only a
      // readout, and the knob needs a track that reads as one.
      style={{ background: ring, padding: expanded ? '5px' : running ? '3px' : '4px' }}
    >
      <div
        className="note-inner"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        // The label says both halves, because for a screen reader the size is
        // the least interesting of the two.
        aria-label={
          expanded ? 'Уменьшить кружочек и выключить звук' : 'Увеличить кружочек и включить звук'
        }
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        {!uploading && (
          <video
            ref={setElement}
            className="note-video"
            src={fileUrl(message.id)}
            // No `poster`: thumbnails are only ever generated for images, and
            // the server falls back to the full file when there is none — so a
            // poster here quietly downloads the whole video, which is the one
            // thing `preload="none"` is here to prevent. The placeholder
            // background on .note-inner covers the frame or two before it runs.
            //
            // Muted to begin with, which is what lets it start at all: a video
            // with sound cannot autoplay. The effect above is what turns the
            // sound on once someone enlarges it.
            muted
            playsInline
            preload="none"
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
            // Gated on the knob: a seek fires timeupdate too, and mid-drag the
            // out-point check would trip the loop and snatch the take back to
            // its in-point under the finger.
            onTimeUpdate={() => {
              if (!scrubbing.current) playback.onTimeUpdate();
            }}
            onEnded={playback.onEnded}
          />
        )}

        {paused && !uploading && (
          <span className="note-play">
            <span className="note-play-circle">
              <PlayIcon />
            </span>
          </span>
        )}

        {/* Not a control — the tap that made it big is what turned this on. It
            is here to answer «почему он вдруг заговорил», and it sits where the
            handoff drew its sound toggle. Only on the enlarged note: a silent
            circle in a thread needs no badge to say it is silent. */}
        {!uploading && expanded && (
          <span className="note-sound" aria-hidden="true">
            <SpeakerIcon size={13} muted={!inView} />
          </span>
        )}

        {uploading && (
          <span className="note-progress">
            <span className="note-spinner" />
            <span className="note-progress-label">{uploading}</span>
          </span>
        )}
      </div>

      {/* The knob, only where the ring is big enough to be a control: an
          enlarged note is being watched, and watched things get seeked. A
          full-circle layer rotates to the arc's angle and the dot rides its
          twelve o'clock, so one transform keeps the two in step; the layer
          takes no pointer events and the dot takes them all, which is what
          leaves the circle itself one tap target exactly as before. */}
      {expanded && !uploading && (
        <div
          className="note-knob"
          style={{ transform: `rotate(${shownPercent * 3.6}deg)` }}
        >
          <span
            className="note-knob-dot"
            role="slider"
            tabIndex={0}
            aria-label="Перемотка"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(shownPercent)}
            aria-valuetext={formatDuration((shownPercent / 100) * playback.lengthMs)}
            onPointerDown={grabKnob}
            onKeyDown={(event) => {
              if (!element) return;
              const step = event.key === 'ArrowLeft' ? -0.05 : event.key === 'ArrowRight' ? 0.05 : 0;
              if (step === 0) return;
              event.preventDefault();
              const to = Math.min(1, Math.max(0, shownPercent / 100 + step));
              element.currentTime = (startMs + to * playback.lengthMs) / 1000;
            }}
          />
        </div>
      )}

      <span className="note-duration">
        {formatDuration(
          running
            ? scrub !== null
              ? (1 - scrub) * playback.lengthMs
              : playback.remainingMs
            : playback.lengthMs,
        )}
      </span>
    </div>
  );
}

/** The three speeds the pill cycles through, in order. */
const SPEEDS = [1, 1.5, 2] as const;

export function VoiceBubble({
  message,
  playing,
  onToggle,
  onEnded,
  canTranscribe,
  onTranscribe,
}: PlaybackProps & {
  /**
   * Whether this deployment has a speech engine at all. A build with none draws
   * no «Аа» at all rather than a button that could only answer 503 — the client
   * is told at sign-in (see Me.transcription in api/client.ts).
   */
  canTranscribe: boolean;
  onTranscribe: () => void;
}) {
  const [element, setElement] = useState<HTMLAudioElement | null>(null);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const playback = usePlayback(element, { message, playing, onEnded }, speed, true);
  const uploading = uploadLabel(message);

  /**
   * Перемотка, on the timeline the bubble already draws: the waveform.
   *
   * The bars used to be decoration — `aria-hidden`, no handlers, coloured in by
   * twenty-sixths — and the position they showed was the only one there was. A
   * tap anywhere along them now puts the playhead there, and holding on scrubs;
   * the maths of «how far along the box is this finger» is lib/scrub.ts.
   *
   * `scrubbing` holds the track's box for as long as the gesture does, and
   * gates onTimeUpdate below: a seek fires timeupdate too, and a late one
   * carrying the old position would drag the playhead back out from under the
   * finger. The element is paused for the drag and plays on from wherever it
   * was let go, which is what makes scrubbing audible rather than a lottery.
   */
  const track = useRef<HTMLDivElement>(null);
  const scrubbing = useRef<DOMRect | null>(null);
  // Whether the drag interrupted playback, so that letting go can resume it —
  // scrubbing a stopped message must not start it talking.
  const resumeOnRelease = useRef(false);

  const seekable = uploading === null && playback.lengthMs > 0;

  const grab = (event: ReactPointerEvent) => {
    if (!seekable || !track.current) return;
    // A right-click is the message's own menu, wherever in the bubble it lands;
    // left unstopped, it reaches the handlers that open it.
    if (event.button !== 0) return;
    // The bubble is also a hold target and a swipe target: without this the
    // 420 ms of a careful scrub open the context menu on top of it, and a
    // horizontal drag doubles as a pull-to-reply.
    event.stopPropagation();
    scrubbing.current = track.current.getBoundingClientRect();
    resumeOnRelease.current = playing;
    element?.pause();
    playback.seekTo(trackFraction(scrubbing.current, event.clientX));
  };

  // On the window rather than on the track, like every drag in this app: the
  // track is 24px tall and a finger crossing it leaves it at once.
  const seekTo = playback.seekTo;
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const rect = scrubbing.current;
      if (!rect) return;
      if (event.cancelable) event.preventDefault();
      seekTo(trackFraction(rect, event.clientX));
    };
    const onUp = () => {
      if (!scrubbing.current) return;
      scrubbing.current = null;
      if (resumeOnRelease.current) void element?.play().catch(() => {});
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [element, seekTo]);

  const bars = waveformFor(message.id);
  const filled = Math.floor((playback.percent / 100) * bars.length);

  /**
   * Whether this reader has opened or closed the text, or has done neither.
   *
   * `null` — neither — is what makes a transcript that already exists visible
   * the moment the thread opens, which is the whole point of storing one. It is
   * per bubble and per session on purpose: it is a way of looking at a message,
   * not a fact about the message, so there is nothing to send anywhere.
   */
  const [open, setOpen] = useState<boolean | null>(null);
  const state = transcriptState(message.transcript);
  const shown = transcriptShown(state, open);
  const action = transcriptAction(state, open);
  const notice = transcriptNotice(state);

  const press = () => {
    // Already running. The block is up saying so, and a second press must not
    // start a second job — nor close the thing the reader is waiting for.
    if (action.busy) return;
    if (action.intent === 'toggle') {
      setOpen(!shown);
      return;
    }
    // Opened first, so the «Распознаём…» line appears under the same press
    // rather than a round trip later.
    setOpen(true);
    onTranscribe();
  };

  return (
    <div className="voice-shell">
      <div className="voice">
        <audio
          ref={setElement}
          src={fileUrl(message.id)}
          preload="none"
          // Gated on the drag, for the reason given where `scrubbing` is
          // declared: a stale timeupdate mid-scrub fights the finger.
          onTimeUpdate={() => {
            if (!scrubbing.current) playback.onTimeUpdate();
          }}
          onEnded={playback.onEnded}
        />

        <button
          type="button"
          className="voice-play"
          aria-label={playing ? 'Пауза' : 'Слушать'}
          disabled={uploading !== null}
          onClick={onToggle}
        >
          {playing ? <PauseIcon size={13} /> : <PlayIcon size={13} />}
        </button>

        <div className="voice-body">
          <div
            ref={track}
            className="voice-track"
            role="slider"
            tabIndex={seekable ? 0 : -1}
            aria-label="Перемотка"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(playback.percent)}
            aria-valuetext={formatDuration(playback.positionMs)}
            aria-disabled={!seekable}
            onPointerDown={grab}
            onKeyDown={(event) => {
              const step =
                event.key === 'ArrowLeft'
                  ? -SCRUB_STEP
                  : event.key === 'ArrowRight'
                    ? SCRUB_STEP
                    : 0;
              if (step === 0 || !seekable) return;
              event.preventDefault();
              playback.seekTo(nudge(playback.percent / 100, step));
            }}
          >
            <div className="voice-wave" aria-hidden="true">
              {bars.map((height, index) => (
                <span
                  key={index}
                  className={`voice-bar${index < filled ? ' voice-bar-played' : ''}`}
                  style={{ height: `${height}px` }}
                />
              ))}
            </div>
            {/* Where the sound actually is, between two bars rather than at the
                edge of one: the bars fill in twenty-sixths and were the only
                readout there was. Nothing to point at on a message still going
                up — there are no bytes to seek into yet. */}
            {seekable && (
              <span
                className="voice-playhead"
                aria-hidden="true"
                style={{ left: `${playback.percent}%` }}
              />
            )}
          </div>
          <div className="voice-meta">
            <span>
              {formatDuration(playback.positionMs)} / {formatDuration(playback.lengthMs)}
            </span>
            {uploading && <span>· {uploading.toLowerCase()}</span>}
          </div>
        </div>

        <div className="voice-side">
          <button
            type="button"
            className="voice-speed"
            aria-label={`Скорость ${speed}×`}
            onClick={(event) => {
              // Its own tap target: changing the speed is not a play command.
              event.stopPropagation();
              setSpeed((current) => SPEEDS[(SPEEDS.indexOf(current) + 1) % SPEEDS.length]!);
            }}
          >
            {speed}×
          </button>

          {/* Nothing to recognise until the bytes are on the server. */}
          {canTranscribe && !uploading && (
            <button
              type="button"
              className={`voice-text${shown ? ' voice-text-on' : ''}`}
              aria-label={action.label}
              aria-expanded={shown}
              onClick={(event) => {
                // Not a play command either — and on a кружочек-sized target the
                // difference between the two is one thumb's width.
                event.stopPropagation();
                press();
              }}
            >
              {action.busy ? <span className="voice-text-spinner" /> : 'Аа'}
            </button>
          )}
        </div>
      </div>

      {shown && (
        <div className="voice-transcript">
          {state === 'ready' ? (
            <p className="voice-transcript-text">{message.transcript?.text}</p>
          ) : (
            <p className="voice-transcript-notice">{notice}</p>
          )}
        </div>
      )}
    </div>
  );
}
